import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PageLayout from "layouts/PageLayout";
import GenericSelect from "components/GenericSelect/GenericSelect";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import TextFieldRender from "components/TextFieldRender/TextFieldRender";
import Button from "components/CustomButtons/Button.js";
import usePopularModels from "hooks/usePopularModels";

const LETTER_FILTERS = [
  "All",
  "#",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

const sectionCardStyle = {
  border: "1px solid #e3e8f1",
  borderRadius: 12,
  backgroundColor: "#fff",
  overflow: "hidden",
};

const actionChipStyle = {
  borderRadius: 10,
  fontWeight: 600,
  backgroundColor: "#f3f6fc",
};

const getSafeLabel = (value) => (value || "").toString().trim();

const getMakeName = (model) => getSafeLabel(model.displayName).toUpperCase() || "UNASSIGNED";

const getModelName = (model) => getSafeLabel(model.modelName) || "Unnamed model";
const getModelImage = (model) => model.pathImage || model.path_image || "/not-available.png";
const getSelectedAppearancesCount = (selectedAppearancesByModel = {}, modelId) =>
  (selectedAppearancesByModel[String(modelId)] || []).length;

const getLetterBucket = (value) => {
  const firstChar = getSafeLabel(value).charAt(0).toUpperCase();
  return /^[A-Z]$/.test(firstChar) ? firstChar : "#";
};

const sortByLabel = (left, right) =>
  `${getMakeName(left)} ${getModelName(left)}`.localeCompare(
    `${getMakeName(right)} ${getModelName(right)}`,
    undefined,
    { sensitivity: "base" }
  );

export default function PopularModels() {
  const {
    dealer,
    vehicleTitleSettingsOpt = [],
    popularModelDestinationOpt = [],
    allPopularModels = [],
    selectedModels = [],
    selectedAppearancesByModel = {},
    formik,
    handleClickCheckbox,
    toggleAppearance,
    isModelSelected,
    isAppearanceSelected,
    clearSelectedModels,
    resetPopularModels,
    hasUnsavedChanges,
  } = usePopularModels();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeLetter, setActiveLetter] = useState("All");
  const [expandedMakes, setExpandedMakes] = useState({});

  const normalizedModels = [...allPopularModels].sort(sortByLabel);
  const selectedPopularModels = normalizedModels.filter((model) => isModelSelected(model.modelId));
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredModels = normalizedModels.filter((model) => {
    const makeName = getMakeName(model);
    const modelName = getModelName(model);
    const matchesSearch =
      !normalizedSearch ||
      makeName.toLowerCase().includes(normalizedSearch) ||
      modelName.toLowerCase().includes(normalizedSearch) ||
      `${makeName} ${modelName}`.toLowerCase().includes(normalizedSearch);
    const matchesLetter = activeLetter === "All" || getLetterBucket(makeName) === activeLetter;

    return matchesSearch && matchesLetter;
  });

  const groupedMakes = filteredModels.reduce((groups, model) => {
    const makeName = getMakeName(model);

    if (!groups[makeName]) {
      groups[makeName] = [];
    }

    groups[makeName].push(model);
    return groups;
  }, {});

  const groupedEntries = Object.keys(groupedMakes)
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
    .map((makeName) => ({
      makeName,
      letter: getLetterBucket(makeName),
      models: groupedMakes[makeName],
      selectedCount: groupedMakes[makeName].filter((model) => isModelSelected(model.modelId)).length,
      selectedAppearanceCount: groupedMakes[makeName].reduce(
        (count, model) => count + getSelectedAppearancesCount(selectedAppearancesByModel, model.modelId),
        0
      ),
    }));

  const makeSections = groupedEntries.reduce((sections, entry) => {
    if (!sections[entry.letter]) {
      sections[entry.letter] = [];
    }

    sections[entry.letter].push(entry);
    return sections;
  }, {});

  const visibleLetters = LETTER_FILTERS.filter((letter) => {
    if (letter === "All") {
      return true;
    }

    return Boolean(makeSections[letter]);
  });

  useEffect(() => {
    const nextExpandedMakes = {};

    groupedEntries.forEach((entry, index) => {
      const shouldOpen = entry.selectedCount > 0 || index < 2;
      nextExpandedMakes[entry.makeName] = shouldOpen;
    });

    if (!groupedEntries.length) {
      setExpandedMakes({});
      return;
    }

    setExpandedMakes((current) => {
      const hasKnownMake = groupedEntries.some((entry) => Object.prototype.hasOwnProperty.call(current, entry.makeName));

      if (hasKnownMake) {
        const preserved = {};
        groupedEntries.forEach((entry, index) => {
          preserved[entry.makeName] =
            current[entry.makeName] !== undefined ? current[entry.makeName] : entry.selectedCount > 0 || index < 2;
        });
        return preserved;
      }

      return nextExpandedMakes;
    });
  }, [allPopularModels.length, searchTerm, activeLetter, selectedModels.length]);

  const toggleMake = (makeName) => {
    setExpandedMakes((current) => ({
      ...current,
      [makeName]: !current[makeName],
    }));
  };

  return (
    <PageLayout>
      <form onSubmit={formik.handleSubmit}>
        <Box mb={4}>
          <Typography variant="h4" style={{ fontWeight: 700, color: "#18233a", marginBottom: 8 }}>
            Popular Models
          </Typography>
          <Typography style={{ color: "#5d6785" }}>
            Select popular models to include in your catalog.
          </Typography>
        </Box>

        <Box mb={3} style={sectionCardStyle}>
          <Box p={3}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={3}>
                <GenericSelect
                  fullWidth
                  options={vehicleTitleSettingsOpt}
                  label="Vehicle Title Settings"
                  name="popularModelsTitleFormat"
                  value={formik.values.popularModelsTitleFormat || ""}
                  onChange={formik.handleChange}
                  error={formik.touched.popularModelsTitleFormat && Boolean(formik.errors.popularModelsTitleFormat)}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={3}>
                <TextFieldRender
                  fullWidth
                  size="small"
                  label="Heading"
                  name="popularModelsHeading"
                  value={formik.values.popularModelsHeading || ""}
                  handleBlur={(value) => formik.setFieldValue("popularModelsHeading", value)}
                  error={formik.touched.popularModelsHeading && Boolean(formik.errors.popularModelsHeading)}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={3}>
                <GenericSelect
                  fullWidth
                  options={popularModelDestinationOpt}
                  label="Popular Model Destination"
                  name="popularModelsDestination"
                  value={formik.values.popularModelsDestination || ""}
                  onChange={formik.handleChange}
                  error={formik.touched.popularModelsDestination && Boolean(formik.errors.popularModelsDestination)}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={3}>
                <TextFieldRender
                  fullWidth
                  size="small"
                  label="Subtitle"
                  name="popularModelsSubtitle"
                  value={formik.values.popularModelsSubtitle || ""}
                  handleBlur={(value) => formik.setFieldValue("popularModelsSubtitle", value)}
                  error={formik.touched.popularModelsSubtitle && Boolean(formik.errors.popularModelsSubtitle)}
                />
              </GridItem>
            </GridContainer>
          </Box>
        </Box>

        <Box mb={3} style={sectionCardStyle}>
          <Box
            p={3}
            display="flex"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            gridGap={16}
          >
            <Box>
              <Typography style={{ fontWeight: 700, color: "#18233a", marginBottom: 12 }}>
                Selected Popular Models ({selectedPopularModels.length})
              </Typography>
              <Box display="flex" flexWrap="wrap" gridGap={8}>
                {selectedPopularModels.length ? (
                  selectedPopularModels.map((model) => (
                    <Chip
                      key={model.modelId}
                      label={`${getModelName(model)} (${getMakeName(model)})`}
                      onDelete={() => handleClickCheckbox(model.modelId)}
                      style={actionChipStyle}
                    />
                  ))
                ) : (
                  <Typography style={{ color: "#77819b" }}>
                    No popular models selected yet.
                  </Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12}>
              <TextField
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search makes or models..."
                variant="outlined"
                size="small"
                style={{ minWidth: 320 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon style={{ color: "#7f8ba6" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="button"
                color="transparent"
                onClick={clearSelectedModels}
                disabled={!selectedPopularModels.length}
              >
                <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
                Clear all
              </Button>
            </Box>
          </Box>

          <Box px={3} pb={2} display="flex" flexWrap="wrap" gridGap={10}>
            {visibleLetters.map((letter) => (
              <Button
                key={letter}
                type="button"
                color={activeLetter === letter ? "primary" : "transparent"}
                onClick={() => setActiveLetter(letter)}
                style={{
                  minWidth: 42,
                  borderRadius: 10,
                  border: activeLetter === letter ? "none" : "1px solid #d7ddeb",
                  color: activeLetter === letter ? "#fff" : "#36415a",
                }}
              >
                {letter}
              </Button>
            ))}
          </Box>

          <Box
            borderTop="1px solid #e9edf5"
            style={{ maxHeight: "calc(100vh - 420px)", overflowY: "auto", overflowX: "hidden" }}
          >
            {groupedEntries.length ? (
              Object.keys(makeSections)
                .sort((left, right) => left.localeCompare(right))
                .map((letter) => (
                  <Box key={letter}>
                    <Box px={3} py={2}>
                      <Typography style={{ fontWeight: 700, color: "#2b3a58" }}>{letter}</Typography>
                    </Box>

                    {makeSections[letter].map((entry) => {
                      const isExpanded = Boolean(expandedMakes[entry.makeName]);

                      return (
                        <Box
                          key={entry.makeName}
                          mx={2}
                          mb={2}
                          style={{ border: "1px solid #e3e8f1", borderRadius: 12, overflow: "hidden" }}
                        >
                          <Box
                            px={2}
                            py={1.5}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            onClick={() => toggleMake(entry.makeName)}
                            style={{ cursor: "pointer", backgroundColor: "#fff" }}
                          >
                            <Box display="flex" alignItems="center" gridGap={8}>
                              <IconButton size="small">
                                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                              </IconButton>
                              <Typography style={{ fontWeight: 700, color: "#18233a" }}>{entry.makeName}</Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gridGap={8}>
                              <Chip label={`${entry.models.length} models`} size="small" style={actionChipStyle} />
                              {entry.selectedCount > 0 && (
                                <Chip
                                  label={`${entry.selectedCount} selected`}
                                  size="small"
                                  style={{ ...actionChipStyle, color: "#1e3a8a" }}
                                />
                              )}
                              {entry.selectedAppearanceCount > 0 && (
                                <Chip
                                  label={`${entry.selectedAppearanceCount} appearances`}
                                  size="small"
                                  style={{ ...actionChipStyle, color: "#065f46" }}
                                />
                              )}
                            </Box>
                          </Box>

                          {isExpanded && (
                            <Box px={3} pb={2}>
                              <GridContainer>
                                {entry.models.map((model) => (
                                  <GridItem key={model.modelId} xs={12} sm={6} md={3}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          color="primary"
                                          checked={isModelSelected(model.modelId)}
                                          onChange={() => handleClickCheckbox(model.modelId)}
                                        />
                                      }
                                      label={getModelName(model)}
                                    />
                                  </GridItem>
                                ))}
                              </GridContainer>

                              {entry.models
                                .filter((model) => isModelSelected(model.modelId))
                                .map((model) => (
                                  <Box
                                    key={`${model.modelId}-appearances`}
                                    mt={2}
                                    pt={2}
                                    borderTop="1px solid #eef2f7"
                                  >
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      flexWrap="wrap"
                                      gridGap={12}
                                      mb={2}
                                    >
                                      <Box display="flex" alignItems="center" gridGap={12}>
                                        <Box
                                          width={56}
                                          height={56}
                                          style={{
                                            borderRadius: 10,
                                            overflow: "hidden",
                                            border: "1px solid #d9e1ef",
                                            backgroundColor: "#f8fafc",
                                          }}
                                        >
                                          <img
                                            src={getModelImage(model)}
                                            alt={getModelName(model)}
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                          />
                                        </Box>
                                        <Box>
                                          <Typography style={{ fontWeight: 700, color: "#18233a" }}>
                                            {getModelName(model)}
                                          </Typography>
                                          <Typography style={{ color: "#77819b", fontSize: 13 }}>
                                            Select one or more appearances for this model.
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Chip
                                        label={`${getSelectedAppearancesCount(selectedAppearancesByModel, model.modelId)} selected`}
                                        size="small"
                                        style={actionChipStyle}
                                      />
                                    </Box>

                                    {Array.isArray(model.appearances) && model.appearances.length ? (
                                      <Box display="flex" flexWrap="wrap" gridGap={12}>
                                        {model.appearances.map((appearance) => {
                                          const selected = isAppearanceSelected(model.modelId, appearance.id);

                                          return (
                                            <Box
                                              key={appearance.id}
                                              width={150}
                                              onClick={() => toggleAppearance(model.modelId, appearance)}
                                              style={{
                                                cursor: "pointer",
                                                borderRadius: 12,
                                                border: selected ? "2px solid #1d4ed8" : "1px solid #d9e1ef",
                                                boxShadow: selected
                                                  ? "0 0 0 3px rgba(29, 78, 216, 0.12)"
                                                  : "0 4px 12px rgba(15, 23, 42, 0.05)",
                                                overflow: "hidden",
                                                backgroundColor: "#fff",
                                              }}
                                            >
                                              <Box
                                                height={110}
                                                style={{
                                                  backgroundColor: "#f8fafc",
                                                  position: "relative",
                                                }}
                                              >
                                                <img
                                                  src={appearance.image || "/not-available.png"}
                                                  alt={`${getModelName(model)} ${appearance.code}`}
                                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                />
                                                <Box
                                                  px={1}
                                                  py={0.5}
                                                  style={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    borderRadius: 999,
                                                    backgroundColor: selected ? "#1d4ed8" : "rgba(255,255,255,0.92)",
                                                    color: selected ? "#fff" : "#475569",
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                  }}
                                                >
                                                  {selected ? "Selected" : "Select"}
                                                </Box>
                                              </Box>
                                              <Box px={1.5} py={1.25}>
                                                <Typography style={{ fontWeight: 700, color: "#18233a", fontSize: 13 }}>
                                                  {appearance.code || "Appearance"}
                                                </Typography>
                                                <Typography style={{ color: "#77819b", fontSize: 12 }}>
                                                  {appearance.id}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    ) : (
                                      <Typography style={{ color: "#77819b", fontSize: 13 }}>
                                        No appearances available for this model.
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))
            ) : (
              <Box p={4} textAlign="center">
                <Typography style={{ fontWeight: 600, color: "#27324a", marginBottom: 8 }}>
                  No models match the current filters.
                </Typography>
                <Typography style={{ color: "#77819b" }}>
                  Adjust the search term or alphabet filter to see more options.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box height={96} />

        <Box
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 20,
            marginLeft: -24,
            marginRight: -24,
            marginBottom: -24,
            backgroundColor: "#fff",
            borderTop: "1px solid #e4e9f2",
            boxShadow: "0 -6px 18px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Box
            px={3}
            py={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gridGap={16}
          >
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12}>
              <Typography style={{ color: "#25324b", fontWeight: 600 }}>
                {selectedPopularModels.length} {selectedPopularModels.length === 1 ? "model" : "models"} selected
              </Typography>
              <Box
                px={1.5}
                py={0.5}
                style={{
                  borderRadius: 999,
                  backgroundColor: hasUnsavedChanges ? "#fff5f5" : "#f4f7fb",
                  color: hasUnsavedChanges ? "#c2410c" : "#7a859f",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {hasUnsavedChanges ? "Unsaved changes" : "No unsaved changes"}
              </Box>
              {dealer?.name && (
                <Typography style={{ color: "#7a859f" }}>
                  {dealer.name}
                </Typography>
              )}
            </Box>

            <Box display="flex" gridGap={12}>
              <Button type="button" color="transparent" onClick={resetPopularModels} disabled={!hasUnsavedChanges}>
                <CloseIcon fontSize="small" style={{ marginRight: 8 }} />
                Cancel
              </Button>
              <Button type="submit" color="rose" disabled={!hasUnsavedChanges}>
                Save changes
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </PageLayout>
  );
}
